import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash, PencilSimple, FloppyDisk, GraduationCap, Eye, EyeSlash } from '@phosphor-icons/react';
import { API } from './adminConstants';

export function DriverTrainingCoursesManagement() {
  const { getAuthHeader } = useAuth();
  const [courses, setCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [form, setForm] = useState({ label: '', value: '', order: 0, is_active: true });

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/driver-training-courses`, { headers: getAuthHeader() });
      setCourses(r.data || []);
    } catch {
      toast.error('Failed to load courses');
    }
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm({ label: '', value: '', order: courses.length, is_active: true });
    setEditCourse(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.label) { toast.error('Course name is required'); return; }
    const payload = { ...form, value: form.value || form.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') };
    try {
      if (editCourse) {
        await axios.put(`${API}/admin/driver-training-courses/${editCourse.id}`, payload, { headers: getAuthHeader() });
        toast.success('Course updated');
      } else {
        await axios.post(`${API}/admin/driver-training-courses`, payload, { headers: getAuthHeader() });
        toast.success('Course added');
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const startEdit = (c) => {
    setEditCourse(c);
    setForm({ label: c.label || '', value: c.value || '', order: c.order || 0, is_active: c.is_active !== false });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/driver-training-courses/${id}`, { headers: getAuthHeader() });
      toast.success('Course deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.put(`${API}/admin/driver-training-courses/${c.id}`, { is_active: !c.is_active }, { headers: getAuthHeader() });
      load();
    } catch {
      toast.error('Toggle failed');
    }
  };

  return (
    <div data-testid="driver-training-courses-management">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-slate-900 flex items-center gap-2">
            <GraduationCap size={26} weight="duotone" className="text-lime-600" />
            Driver Training Courses
          </h1>
          <p className="text-slate-500 text-sm">
            The list of courses shown in the secondary dropdown on the contact form when a visitor selects <strong>Driver Training Academy</strong>.
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-course-btn">
          <Plus size={15} className="mr-1" /> Add course
        </Button>
      </div>

      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="course-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editCourse ? 'Edit course' : 'New course'}</h3>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Course name *</Label>
                <Input
                  value={form.label}
                  onChange={e => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g. HGV Class 1 (Cat C+E)"
                  data-testid="course-label"
                />
              </div>
              <div>
                <Label className="text-xs">Display order</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  min="0"
                  data-testid="course-order"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Slug / Value (auto-generated if blank)</Label>
                <Input
                  value={form.value}
                  onChange={e => setForm({ ...form, value: e.target.value })}
                  placeholder="hgv-class-1"
                  data-testid="course-value"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-slate-300 text-lime-600 focus:ring-lime-500"
                    data-testid="course-active"
                  />
                  Active (visible to visitors)
                </label>
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-5">
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-course-btn">
              <FloppyDisk size={15} className="mr-1" />{editCourse ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Order</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Course</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Slug</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {courses.map(c => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`course-row-${c.id}`}>
                <td className="px-4 py-3 text-sm text-slate-500">{c.order}</td>
                <td className="px-4 py-3 text-sm font-medium text-slate-900">{c.label}</td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono text-xs">{c.value}</td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleActive(c)}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${c.is_active ? 'bg-lime-100 text-lime-700' : 'bg-slate-100 text-slate-500'}`}
                    data-testid={`course-toggle-${c.id}`}
                  >
                    {c.is_active ? <Eye size={11} weight="fill" /> : <EyeSlash size={11} weight="fill" />}
                    {c.is_active ? 'Visible' : 'Hidden'}
                  </button>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(c)} className="h-7 w-7 p-0" data-testid={`course-edit-${c.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700" data-testid={`course-delete-${c.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {courses.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No courses yet — click <strong>Add course</strong> to create the first one.</div>}
      </div>
    </div>
  );
}
